import { createSelector } from "reselect"
import { keyBy, get, map, mapValues, chain, snakeCase, some, size, isEmpty, includes, toLower } from "lodash"
import { getStartDate, computeStartDate } from "helpers/dates"
import { getPortfolioLive } from "modules/portfolio-root/selectors/portfolios"

const EMPTY_ARRAY = []
const getRawModelsCoverage = state => get(state, "global.data.modelsCoverage.data", EMPTY_ARRAY)
const getSelectedModelId = (_, props) => get(props, "modelId")
export const getResourceType = (_, props) => {
  const resourceType = toLower(get(props, "resourceType"))
  return resourceType === "basket" ? "swap" : resourceType
}
const getNoRestrictionModels = (_, props) => get(props, "noRestrictionModels", EMPTY_ARRAY)
const getRawModels = state => get(state, "global.data.modelsAvailability.data.models", EMPTY_ARRAY)
const getEarliestUploadedDate = (_, props) => get(props, "assetAvailability.earliest")
const getLatestUploadedDate = (_, props) => get(props, "assetAvailability.latest")

function getPeriod(models) {
  const periodSelector = Math.floor(size(models) / 5)
  let period
  switch (periodSelector) {
    case 0:
      period = "1Y"
      break
    case 1:
      period = "6M"
      break
    case 2:
    case 3:
      period = "3M"
      break
    default:
      period = "1M"
  }
  return period
}

export const getModels = createSelector(
  [getRawModels],
  (models) => keyBy(models, "id"),
)

const getModelsResourceCoverage = createSelector(
  [getRawModelsCoverage, getResourceType],
  (models, resourceType) => {
    switch (resourceType) {
      case "portfolio":
      case "swap":
      case "etf":
      case "index":
        return mapValues(models, model => chain(model)
          .get("simulation.coverage")
          .some(cov => get(cov, "summary.percentGmvAvailable", 0) !== 0)
          .value())
      case "security":
        return mapValues(models, model => !!get(model, "security.descriptors.modelProviderId"))
    }

    return null
  },
)

export const getSortedModelsByCoverage = createSelector(
  [getModelsResourceCoverage, getModels, getSelectedModelId, getNoRestrictionModels],
  (modelsResourceCoverage, models, selectedModelId, noRestrictionModels) => chain(models)
    .map(model => ({
      ...model,
      hasCoverage: selectedModelId === model.id
        || includes(noRestrictionModels, model.id)
        || some(modelsResourceCoverage, (coverageValue, key) => snakeCase(model.id) === key && !!coverageValue),
    }))
    .orderBy(["hasCoverage", "name"], ["desc", "asc"])
    .value(),
)

export const getModelsDates = createSelector(
  [getLatestUploadedDate, getEarliestUploadedDate, getModels, getPortfolioLive, getResourceType],
  (latestUploadedDate, earliestUploadedDate, models, live, resourceType) => {
    if (isEmpty(models)) {
      return []
    }

    switch (resourceType) {
      case "portfolio":
        if (!latestUploadedDate || !earliestUploadedDate) {
          return []
        }
        return map(models, model => {
          let endDate = get(model, "availability.currentDate")
          if (!live && latestUploadedDate < endDate) {
            endDate = latestUploadedDate
          }
          const period = getPeriod(models)
          const startDate = getStartDate(endDate, period)
          return {
            id: model.id,
            from: computeStartDate(earliestUploadedDate, startDate),
            to: endDate,
          }
        })
      case "security":
        return map(models, model => {
          const endDate = get(model, "availability.currentDate")

          return {
            id: model.id,
            from: endDate,
            to: endDate,
          }
        })
      case "swap":
      case "etf":
      case "index":
        return map(models, model => {
          const endDate = get(model, "availability.currentDate")

          const period = getPeriod(models)
          const startDate = getStartDate(endDate, period)
          return {
            id: model.id,
            from: computeStartDate(earliestUploadedDate, startDate),
            to: endDate,
          }
        })
    }
  },
)
